import { Component, Input, ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { WebShopService } from "../../services/webShop.service";
import { UserHelperService } from "../../services/userHelper.service";
import { CoreLogService } from "../../services/log.service";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import Utils from "../../shared/utils";

/**
 * Reusable Newsletter Checkbox Component
 * Manages newsletter subscription state and visibility automatically
 * Used in basket, registration, and checkout for newsletter opt-in
 */
@Component({
  selector: "ya-newsletter-checkbox",
  templateUrl: "./newsletter.component.html",
  styleUrls: ["./newsletter.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsletterCheckboxComponent implements OnInit, OnDestroy {

  /**Controls if newsletter opt-in is available */
  public newsletter_show_optin: boolean = false;
  /** Newsletter text to display */
  public newsletterText: SafeHtml = "";

  /** Checkbox state - managed internally */
  public checked: boolean = false;

  /** Controls if component is visible */
  public isVisible: boolean = false;

  /**
   * If true: Only show if user is registered and hasn't subscribed (basket, checkout)
   * If false: Always show newsletter checkbox (registration)
   */
  @Input() checkRegistrationStatus: boolean = true;

  private languageChangeSubscription: Subscription;

  constructor(
    private _webShopService: WebShopService,
    private _userHelperService: UserHelperService,
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _internationalisationService: InternationalisationService,
  ) { }

  async ngOnInit(): Promise<void> {
    const me = this;
    me.loadNewsletterOptinSetting();
    me.loadNewsletterText();
    me.loadInitialCheckState();
    me.subscribeToLanguageChanges();
    me.checkVisibility().then(() => {
      me.PushChanges();
    }).catch(err => {
      me._coreLogService.error("NewsletterCheckboxComponent.ngOnInit", "checkVisibility failed", err);
    });
    me.PushChanges();
  }

  ngOnDestroy(): void {
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }

  /**
   * Subscribe to language changes and reload newsletter text
   */
  private subscribeToLanguageChanges(): void {
    const me = this;
    me.languageChangeSubscription = me._internationalisationService.localeChanged.subscribe(() => {
      me.loadNewsletterText();
      me.PushChanges();
    });
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("NewsletterCheckboxComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  /**
   * Checks if newsletter checkbox should be visible
   */
  private async checkVisibility(): Promise<void> {
    const me = this;
    if (!me.checkRegistrationStatus) {
      // Don't check registration status & display checkbox automatically (e.g., registration page)
      me.isVisible = true;
      return;
    }

    // Check if user is registered and hasn't subscribed (e.g., basket, checkout)
    const isRegistered = me._userHelperService.IsRegistered;
    const canDisplay = await me._webShopService.checkCanDisplayNewsletter(isRegistered);
    me.isVisible = canDisplay;
    me.PushChanges();
  }

  /**
   * Loads the initial checked state from WebShopService
   * If user already checked newsletter in basket, it will be checked in checkout too
   */
  private loadInitialCheckState(): void {
    const me = this;
    me.checked = me._webShopService.hasAcceptedNewsletter;
  }

  private loadSettingViewModel() {
    const me = this;
    let settingsViewModel = null;
    const resolverData = me._activatedRoute.snapshot.data["settingsViewModel"];
    if (resolverData) {
      settingsViewModel = resolverData["settingsViewModel"];

    }
    return settingsViewModel;
  }

  private loadNewsletterOptinSetting() {
    const me = this;
    const settingsViewModel = me.loadSettingViewModel();
    if (settingsViewModel) {
      me.newsletter_show_optin = settingsViewModel.newsletter_show_optin;
      me.PushChanges();
    }

  }

  /**
   * Loads and sanitizes newsletter text from settings or resolver
   */
  private loadNewsletterText(): void {
    const me = this;
    let resolverNewsletterText: string = null;
    const settingsViewModel = me.loadSettingViewModel();
    if (me.newsletter_show_optin) {
      if (settingsViewModel && settingsViewModel.newsletter_text) {
        resolverNewsletterText = settingsViewModel.newsletter_text;
      }
    }
    try {
      let newsletterTextRaw: string = resolverNewsletterText;
      if (me.newsletter_show_optin && newsletterTextRaw) {
        me.newsletterText = Utils.replaceJsonLinks(newsletterTextRaw);
        me._changeDetectorRef.markForCheck();
      }
    } catch (err) {
      me._coreLogService.error('NewsletterCheckboxComponent', 'loadNewsletterText', err);
    }
  }

  /**
   * Handles checkbox change event
   * @param checked Checkbox state
   */
  public onCheckboxChange(checked: boolean): void {
    const me = this;
    me.checked = checked;
    // Save state to WebShopService so it persists across navigation (basket -> checkout)
    me._webShopService.hasAcceptedNewsletter = checked;
    try {
      me._changeDetectorRef.detectChanges();
    } catch (e) {
      me._coreLogService.error("NewsletterCheckboxComponent.onCheckboxChange", "detectChanges failed", e);
    }
  }

  /**
   * Handles click on newsletter text - prevents event bubbling for links
   * @param event Click event
   */
  public onNewsletterTextClick(event: any): void {
    if (event.target.tagName === 'A') {
      event.stopPropagation();
    }
  }

  /**
   * Sends newsletter opt-in to backend if checkbox is checked
   * Call this method when submitting the form (e.g., registration, checkout)
   * @returns Promise<boolean> - true if successful or not checked, false on error
   */
  public async submitNewsletterOptIn(): Promise<boolean> {
    const me = this;
    if (!me.checked) {
      return Promise.resolve(true); // Not checked, nothing to do
    }
    try {
      const success = await me._webShopService.SetNewsletter(true);
      if (!success) {
        me._coreLogService.error("NewsletterCheckboxComponent.submitNewsletterOptIn", "Newsletter Opt-In failed without exception");
      }
      return success;
    } catch (err) {
      me._coreLogService.error("NewsletterCheckboxComponent.submitNewsletterOptIn", "Newsletter Opt-In Exception", err);
      return false;
    }
  }
}
